import { Box, Grid } from '@mui/material';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmployeeSelect } from '~/components/GenericForm/EmployeeSelect';
import { CostCenter } from '~/data/costCenters';
import { EmpPosition } from '~/data/employee/types';
import { EmployeesIndexed } from '~/data/employees';
import { AllEnums } from '~/data/enums';
import { LegalEntity } from '~/data/legalEntity';
import { OrgTreeData, OrganizationUnit } from '~/data/orgunits/types';
import { UnitPosition } from '~/data/unitPositions';
import { resetEnumsFormValues } from '~/features/ScheduledEmploymentChanges/helpers';
import {
  CostCenterComponent,
  InternalPositionComponent,
  ManagerComponent,
  UnitPositionComponent,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components';
import { ScheduledEmploymentUnitSelection } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import {
  ChangedStartDateState,
  Form,
  Restrict,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import {
  LegacyAutocompleteField,
  LegacyDateField,
  LegacyInputField,
} from '~/legacy-components/LegacyForms';

import { useSxStyles } from './useStyles';

type Props = {
  scheduleFormMethods: UseFormReturn<Form>;
  index: number;
  empData: EmpPosition;
  legalEntities: LegalEntity[];
  allEnums: AllEnums;
  unitPositions: UnitPosition[];
  unitSelection: ScheduledEmploymentUnitSelection;
  managers: number[];
  allEmployees: EmployeesIndexed;
  orgTree: OrgTreeData[];
  restrictField: Restrict;

  newEmpl: boolean;
  allResponsibleHRIds: number[];
  pendingProposal: boolean;
  inProgressPropStartDate?: number;
  hrSelectorDisable: boolean;
  newEngine: boolean;

  getCostCenterValues: () => CostCenter[];
  onUnitChange: (newValue: number) => void;
  onDialogChange: (activeDialogName: string) => void;
  onLegalEntityChange: (newValue: string) => void;
  setWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setChangedStartDate: React.Dispatch<
    React.SetStateAction<ChangedStartDateState>
  >;
  deleteSelectedCC: (id: number) => void;
};

export const FormComponent = (props: Props) => {
  const {
    scheduleFormMethods,
    getCostCenterValues,
    empData,
    legalEntities,
    allEnums,
    onUnitChange,
    unitPositions,
    unitSelection,
    onDialogChange,
    managers,
    allEmployees,
    onLegalEntityChange,
    setWarningOpen,
    orgTree,
    restrictField,
    index,
    deleteSelectedCC,
    newEmpl,
    setChangedStartDate,
    allResponsibleHRIds,
    pendingProposal,
    inProgressPropStartDate,
    hrSelectorDisable,
    newEngine,
  } = props;

  const {
    formState: { errors },
    getValues,
    control,
    setValue,
  } = scheduleFormMethods;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const requiredText = t('fieldIsRequired');

  const startDateMinDate = useMemo(() => {
    const employmentStartDate = getValues(
      //@ts-ignore
      `schedule.${index}.employment.fEmplDateStart`,
    );

    if (inProgressPropStartDate && employmentStartDate) {
      return moment(inProgressPropStartDate).isBefore(employmentStartDate)
        ? employmentStartDate
        : moment(inProgressPropStartDate);
    } else if (!inProgressPropStartDate && employmentStartDate) {
      return employmentStartDate;
    } else if (!employmentStartDate && inProgressPropStartDate)
      return moment(inProgressPropStartDate);
    else return undefined;
  }, [getValues, inProgressPropStartDate, index]);

  const openWarning = useCallback(
    (v: moment.Moment | null) => {
      if (!v) {
        return;
      }
      if (moment(v).isBefore(new Date())) {
        setWarningOpen(true);
      }
    },
    [setWarningOpen],
  );
  const BusinessUnits = useMemo(
    () => (
      <>
        {/* INFO: The first is the main org tree, that falls under UNIT not business unit */}
        {orgTree.slice(1).map((bu) => {
          return (
            <Grid item key={bu.fTreeId}>
              {/* @ts-ignore */}
              <Controller
                control={control}
                name={`schedule.${index}.fEmpBusinessUnit${bu.fTreeId}`}
                render={({ field: { onChange, value } }) => (
                  <LegacyAutocompleteField
                    options={bu.units}
                    getOptionLabel={(o) => o.fTreeUnitName}
                    isOptionEqualToValue={(o, v) =>
                      o.fTreeUnitId === v.fTreeUnitId
                    }
                    onChange={(_, value) => onChange(value?.fTreeUnitId || '')}
                    value={
                      bu.units.find((o) => o.fTreeUnitId === value) || null
                    }
                    sx={sxStyles.selectStyle}
                    label={bu.fTreeName}
                  />
                )}
              />
            </Grid>
          );
        })}
      </>
    ),
    [sxStyles.selectStyle, control, index, orgTree],
  );

  const { unitOptions, groupToMyUnitsAndOthers } = useMemo(() => {
    const optData = unitSelection.options;
    const myUnitIds = optData.myUnits.map((u) => u.fOrgUnitId);

    const groupToMyUnitsAndOthers = (option: unknown) => {
      const opt = option as OrganizationUnit;

      if (optData.allUnitOptionsEnabled) {
        return myUnitIds.includes(opt.fOrgUnitId)
          ? t('myUnits')
          : t('otherUnits');
      }

      return '';
    };

    const unitOptions = optData.allUnitOptionsEnabled
      ? [...optData.myUnits, ...optData.otherUnits]
      : optData.myUnits;

    return {
      unitOptions,
      groupToMyUnitsAndOthers,
    };
  }, [unitSelection.options, t]);

  return (
    <Grid container columnSpacing={1}>
      <Grid item>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fEmpLegalEntity`}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value } }) => (
            <LegacyAutocompleteField
              disabled={restrictField.manager && !restrictField.hr}
              required
              sx={sxStyles.selectStyle}
              placeholder={' '}
              options={legalEntities}
              getOptionLabel={(c) =>
                `${c.fLegalEntityName} (${c.fLegalEntityCode})`
              }
              onChange={(e, value) => {
                const fLegalEntityId = value?.fLegalEntityId.toString() || '';
                onChange(fLegalEntityId);
                onLegalEntityChange(fLegalEntityId);
                resetEnumsFormValues(
                  fLegalEntityId,
                  getValues(
                    `schedule.${index}.empPosition.fInternalPositionUnit`,
                  ),
                  allEnums,
                  index,
                  scheduleFormMethods,
                  legalEntities,
                );
              }}
              value={
                legalEntities.find(
                  (le) => le.fLegalEntityId.toString() === value,
                ) || null
              }
              label={t('fEmpLegalEntity')}
              fieldError={
                errors.schedule?.[index]?.empPosition?.fEmpLegalEntity
              }
            />
          )}
        />
      </Grid>
      <Grid item>
        <InternalPositionComponent
          index={index}
          control={control}
          getValues={getValues}
          allEnums={allEnums}
          errors={errors}
          setValue={setValue}
        />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fEmpJobTitle`}
          render={({ field: { onChange, value } }) => (
            <LegacyInputField
              value={value}
              sx={sxStyles.selectStyle}
              onChange={onChange}
              label={t('fEmpJobTitle')}
            />
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fInternalPositionInfo`}
          render={({ field: { onChange, value } }) => (
            <LegacyInputField
              inputProps={{ maxLength: 80 }}
              value={value}
              sx={sxStyles.selectStyle}
              onChange={onChange}
              label={t('laInfo')}
            />
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fInternalPositionUnit`}
          rules={{
            validate: (value) => (value && value !== -1) || requiredText,
          }}
          render={({ field: { onChange, value } }) => (
            <LegacyAutocompleteField
              required
              sx={sxStyles.selectStyle}
              placeholder={''}
              options={unitOptions}
              groupBy={groupToMyUnitsAndOthers}
              getOptionLabel={(c) => `${c.fOrgUnitName}, ${c.fOrgUnitNumber}`}
              onChange={(e, value) => {
                onChange(value?.fOrgUnitId || -1);
                onUnitChange(value?.fOrgUnitId || -1);
                resetEnumsFormValues(
                  getValues(`schedule.${index}.empPosition.fEmpLegalEntity`),
                  value?.fOrgUnitId || -1,
                  allEnums,
                  index,
                  scheduleFormMethods,
                  legalEntities,
                );
              }}
              value={
                unitOptions.find((units) => units.fOrgUnitId === value) || null
              }
              label={t('laUnit')}
              fieldError={
                errors.schedule?.[index]?.empPosition?.fInternalPositionUnit
              }
            />
          )}
        />
      </Grid>
      {index === 0 && <>{BusinessUnits}</>}
      <Grid item>
        <ManagerComponent
          index={index}
          control={control}
          managers={managers}
          allEmployees={allEmployees}
          errors={errors}
        />
      </Grid>
      <Grid item>
        <UnitPositionComponent
          index={index}
          control={control}
          getValues={getValues}
          unitPositions={unitPositions}
          empData={empData}
        />
        <Box
          sx={{
            ...sxStyles.cssLabelDiv,
            ...sxStyles.linkStyle,
            ...sxStyles.highlightColor,
            ...sxStyles.linkHover,
          }}
        >
          <label onClick={() => onDialogChange('unitPosition')}>
            <i className="fa fa-plus-circle"></i>
            <em>{t('laPercUnitPosition')}</em>
          </label>
        </Box>
      </Grid>
      <Grid item>
        <CostCenterComponent
          index={index}
          control={control}
          getValues={getValues}
          getCostCenterValues={getCostCenterValues}
          errors={errors}
          deleteSelectedCC={deleteSelectedCC}
        />
        <Box
          sx={{
            ...sxStyles.cssLabelDiv,
            ...sxStyles.linkStyle,
            ...sxStyles.highlightColor,
            ...sxStyles.linkHover,
          }}
        >
          <label onClick={() => onDialogChange('costCenter')}>
            <i className="fa fa-plus-circle"></i>
            <em>{t('laPercCostCenter')}</em>
          </label>
        </Box>
      </Grid>
      <Grid item>
        <Controller
          name={`schedule.${index}.empPosition.fInternalPositionStartDate`}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <LegacyDateField
              disabled={newEmpl || pendingProposal}
              minDate={startDateMinDate}
              maxDate={
                moment(
                  getValues(
                    `schedule.${index}.employment.fEmplLastDateEmployment`,
                  ),
                ) || undefined
              }
              inputSx={sxStyles.dateInput}
              value={value}
              onChange={(v: moment.Moment | null) => {
                openWarning(v);
                onChange(v || null);
                setChangedStartDate((activeValue) => ({
                  ...activeValue,
                  empPositionStartDate: true,
                }));
              }}
              label={t('laStartDate')}
              required
              fieldError={
                errors.schedule?.[index]?.empPosition
                  ?.fInternalPositionStartDate
              }
            />
          )}
        />
      </Grid>
      {index === 0 && newEngine && (
        <Grid item xs={6} sx={{ mt: 2 }}>
          <Controller
            name={`schedule.${index}.empPosition.fInternalPositionProposalHrId`}
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <EmployeeSelect
                  FormControlProps={{ fullWidth: true, error: !!error }}
                  label={t('laHRResponsible')}
                  value={value || ''}
                  onChange={onChange}
                  optionEmployeeIds={allResponsibleHRIds}
                  fullWidth={true}
                  required={true}
                  disabled={pendingProposal || hrSelectorDisable}
                  size="small"
                />
              );
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};
